import { getDeeplyNestedValue } from "@multiconsult-group/multiplacer-core";
import { selectCompCodePath } from "../globalSelectors";
import { createSelector } from "@reduxjs/toolkit";
import type { IRootState } from "../app/store";
import { selectViewerPixPolygons, selectHighlightedViewerPixPolygons } from "../DesignTab/pixpolygonSelectors";
import { selectMcBimItemsForViewer } from "../McBim/mcbimItemSelectors";
import { selectHttpMcBimCeilingItems } from "../McBim/httpMcBimSelectors";
import { selectHttpDevices } from "../DesignTab/devices/deviceSelectors";
import { selectNonPublishedDevices } from "../DesignTab/devices/deviceSlice";
import { selectArticleColorsByCompCode, selectArticleFallbackColor } from "./articleColoringSelectors";
import { selectStreetViewItemRenderer, selectStreetViewItemsById } from "./viewerSelectors";
import { selectSceneLocal3dItems } from "./viewerSceneSelectors";
import { CEILING_DEVICE_3D_ID_PREFIX, PREVIEW_DEVICE_3D_ID_PREFIX } from "./ceilingDevice3dSelectors";
import { selectHttpMcBimItemsInSelectedRoom } from "../RoomSideBar/singleRoomSelectors";
import { selectRoomSectionPerimeterItems } from "../RoomSectionViews/roomSectionPerimeterSelectors";
import { selectIsoViewerRenderItems } from "../RoomSectionViews/roomSectionViewSelectors";

export const selectArticleColorsByObjectId = createSelector(
  [selectArticleColorsByCompCode, selectMcBimItemsForViewer, selectHttpMcBimCeilingItems,
    selectHttpDevices, selectNonPublishedDevices, selectArticleFallbackColor],
  (colors, items, ceilings, devices, previews, fallbackColor) => {
    const byId = new Map<string, string>();
    const add = (id: unknown, code: string | null | undefined) => {
      const color = (code ? colors.get(code.trim()) : undefined) ?? fallbackColor;
      if (color && id != null) byId.set(String(id), color);
    };
    for (const item of [...items, ...ceilings, ...(devices ?? [])]) add(item._id, item.compCode);
    previews.forEach((device, index) => add(device._id ?? `draft-${index}`, device.compCode));
    return byId;
  },
);

// Apply article colors after visibility has been resolved. Query arguments depend
// only on the uncolored scene, never on the API response or colored polygons.
export const selectArticleColoredViewerPolygons = createSelector(
  [selectViewerPixPolygons, selectHighlightedViewerPixPolygons, selectArticleColorsByObjectId],
  (base, highlighted, colors) => {
    if (!colors.size) return highlighted;
    return highlighted.map((polygon, index) => {
      const color = colors.get(String(polygon.entityId ?? polygon.id));
      // Preserve search/room highlight fills and all selection outlines.
      return color && polygon.fillColor === base[index]?.fillColor
        ? { ...polygon, fillColor: color } : polygon;
    });
  },
);

export const selectArticleColoredStreetViewRenderer = createSelector(
  [selectStreetViewItemRenderer, selectStreetViewItemsById, selectArticleColorsByCompCode,
    (state: IRootState) => state.viewer.clickedMcBimItemId, selectArticleFallbackColor],
  (render, items, colors, selectedId, fallbackColor) => (item: Parameters<typeof render>[0]) => {
    const rendered = render(item);
    const code = items.get(String(item._id))?.compCode;
    const color = (code ? colors.get(code.trim()) : undefined) ?? fallbackColor;
    return color && String(item._id) !== selectedId ? { ...rendered, color } : rendered;
  },
);

export const selectArticleColoredLocal3dItems = createSelector(
  [selectSceneLocal3dItems, selectArticleColorsByObjectId,
    (state: IRootState) => state.device.selectedDeviceId],
  (items, colors, selectedId) => items.map(rendered => {
    const id = String(rendered.item._id);
    const prefix = id.startsWith(CEILING_DEVICE_3D_ID_PREFIX) ? CEILING_DEVICE_3D_ID_PREFIX
      : id.startsWith(PREVIEW_DEVICE_3D_ID_PREFIX) ? PREVIEW_DEVICE_3D_ID_PREFIX : null;
    if (!prefix) return rendered;
    const deviceId = id.slice(prefix.length);
    const color = colors.get(deviceId);
    return color && deviceId !== selectedId ? { ...rendered, color } : rendered;
  }),
);

export const selectArticleColoredIsoItems = createSelector(
  [selectIsoViewerRenderItems, selectArticleColorsByCompCode, selectHttpMcBimItemsInSelectedRoom, selectRoomSectionPerimeterItems, selectCompCodePath, selectArticleFallbackColor],
  (items, colors, roomItems, perimeterItems, compCodePath, fallbackColor) => {
    const codes = new Map([...(roomItems ?? []), ...(perimeterItems ?? [])].map(item => {
      const code = "compCode" in item ? item.compCode : compCodePath ? getDeeplyNestedValue(item, compCodePath) : null;
      return [String(item._id), typeof code === "string" ? code : null];
    }));
    return items.map(rendered => {
      const code = codes.get(String(rendered.item._id));
      const color = (code ? colors.get(code.trim()) : undefined) ?? fallbackColor;
      return color ? { ...rendered, color } : rendered;
    });
  },
);
